(() => {
  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[–—]/g, '-')
      .replace(/\s*\/\s*/g, ' / ')
      .replace(/\s+/g, ' ');
  }

  function getProductContainer(element) {
    return (
      element.closest(
        '[data-section-type*="product"], product-form, .product-page, .product__content'
      ) ||
      document.querySelector(
        '[data-section-type*="product"], product-form, .product-page, .product__content'
      ) ||
      document
    );
  }

  function selectRadioVariant(productContainer, variantName) {
    const target = normalizeText(variantName);

    const inputs = productContainer.querySelectorAll(
      [
        'input[type="radio"][name*="option"]',
        'input[type="radio"][name*="Option"]',
        'input[type="radio"][data-single-option-selector]',
        'input[type="radio"][data-swatch-input]'
      ].join(',')
    );

    for (const input of inputs) {
      const inputValue = normalizeText(input.value);

      let label = null;

      if (input.id) {
        try {
          label = productContainer.querySelector(
            `label[for="${CSS.escape(input.id)}"]`
          );
        } catch (error) {
          label = document.querySelector(
            `label[for="${CSS.escape(input.id)}"]`
          );
        }
      }

      const labelValue = normalizeText(label?.textContent);

      if (inputValue === target || labelValue === target) {
        input.checked = true;

        input.dispatchEvent(
          new Event('input', {
            bubbles: true
          })
        );

        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );

        return label || input;
      }
    }

    return null;
  }

  function selectDropdownVariant(productContainer, variantName) {
    const target = normalizeText(variantName);

    const selects = productContainer.querySelectorAll(
      [
        'select[name*="options"]',
        'select[data-single-option-selector]',
        'variant-selects select',
        'select[name="id"]'
      ].join(',')
    );

    for (const select of selects) {
      const matchingOption = Array.from(select.options).find((option) => {
        const optionText = normalizeText(option.textContent);
        const optionValue = normalizeText(option.value);

        return optionText === target || optionValue === target;
      });

      if (!matchingOption) continue;

      select.value = matchingOption.value;

      select.dispatchEvent(
        new Event('input', {
          bubbles: true
        })
      );

      select.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );

      return select;
    }

    return null;
  }

  function selectClickableSwatch(productContainer, variantName) {
    const target = normalizeText(variantName);

    const candidates = productContainer.querySelectorAll(
      [
        '[data-value]',
        '[data-option-value]',
        '[data-swatch]',
        '[data-swatch-value]',
        'button',
        'label'
      ].join(',')
    );

    for (const candidate of candidates) {
      const possibleValues = [
        candidate.dataset.value,
        candidate.dataset.optionValue,
        candidate.dataset.swatch,
        candidate.dataset.swatchValue,
        candidate.getAttribute('aria-label'),
        candidate.getAttribute('title'),
        candidate.textContent
      ];

      const matches = possibleValues.some(
        (value) => normalizeText(value) === target
      );

      if (!matches) continue;

      candidate.click();
      return candidate;
    }

    return null;
  }

  function selectShadeVariant(shadeFinder, variantName) {
    const productContainer = getProductContainer(shadeFinder);

    return (
      selectRadioVariant(productContainer, variantName) ||
      selectDropdownVariant(productContainer, variantName) ||
      selectClickableSwatch(productContainer, variantName)
    );
  }

  function initializeShadeFinder(shadeFinder) {
    if (shadeFinder.dataset.initialized === 'true') return;

    shadeFinder.dataset.initialized = 'true';

    const openButton = shadeFinder.querySelector(
      '[data-shade-finder-open]'
    );

    const modal = shadeFinder.querySelector(
      '[data-shade-finder-modal]'
    );

    const closeButtons = shadeFinder.querySelectorAll(
      '[data-shade-finder-close]'
    );

    const shadeCards = shadeFinder.querySelectorAll(
      '[data-shade-choice]'
    );

    const nextButton = shadeFinder.querySelector(
      '[data-shade-finder-next]'
    );

    let selectedVariantName = '';
    let selectedCard = null;

    function openModal() {
      modal.hidden = false;
      document.body.classList.add('facelove-shade-modal-open');

      window.requestAnimationFrame(() => {
        const firstCard = shadeFinder.querySelector(
          '[data-shade-choice]'
        );

        firstCard?.focus();
      });
    }

    function closeModal() {
      modal.hidden = true;
      document.body.classList.remove('facelove-shade-modal-open');
      openButton?.focus();
    }

    shadeCards.forEach((card) => {
      card.addEventListener('click', () => {
        shadeCards.forEach((otherCard) => {
          otherCard.classList.remove('is-selected');
          otherCard.setAttribute('aria-pressed', 'false');
        });

        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');

        selectedCard = card;
        selectedVariantName = card.dataset.variantName || '';

        nextButton.disabled = false;
        nextButton.textContent = `Your Perfect Shade is: ${selectedVariantName}`;
      });
    });

    nextButton.addEventListener('click', () => {
      if (!selectedVariantName) return;

      const selectedElement = selectShadeVariant(
        shadeFinder,
        selectedVariantName
      );

      if (!selectedElement) {
        console.warn(
          `FACELOVE Shade Finder: Could not find a Palo Alto variant option matching "${selectedVariantName}".`
        );

        nextButton.textContent =
          `Could not find ${selectedVariantName}`;

        return;
      }

      closeModal();

      window.setTimeout(() => {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        window.setTimeout(() => {
          if (typeof selectedElement.focus === 'function') {
            selectedElement.focus({
              preventScroll: true
            });
          }

          selectedElement.classList.add(
            'facelove-shade-variant-focus'
          );

          window.setTimeout(() => {
            selectedElement.classList.remove(
              'facelove-shade-variant-focus'
            );
          }, 1800);
        }, 450);
      }, 100);

      if (selectedCard) {
        selectedCard.classList.remove('is-selected');
        selectedCard.setAttribute('aria-pressed', 'false');
      }

      selectedVariantName = '';
      selectedCard = null;
      nextButton.disabled = true;
      nextButton.textContent = 'Select a complexion';
    });

    openButton.addEventListener('click', openModal);

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });
  }

    function updateSelectedShadeTitle(scope = document) {
  const checkedInputs = scope.querySelectorAll(
    'input[type="radio"]:checked'
  );

  checkedInputs.forEach((checkedInput) => {
    const selectedValue = checkedInput.value?.trim();

    if (!selectedValue) return;

    const swatchGroup =
      checkedInput.closest(
        [
          'fieldset',
          '.radio__fieldset',
          '.variant-input-wrap',
          '.variant-wrapper',
          '.product__option',
          '.product__selector',
          '.selector-wrapper',
          '[data-option-index]',
          '[data-option-position]'
        ].join(',')
      ) ||
      checkedInput.parentElement?.parentElement;

    if (!swatchGroup) return;

    const allInputs = swatchGroup.querySelectorAll(
      'input[type="radio"]'
    );

    const shadeValues = [
      'Fair / Light',
      'Natural / Medium',
      'Bronze / Caramel'
    ].map(normalizeText);

    const isShadeGroup = Array.from(allInputs).some((input) =>
      shadeValues.includes(normalizeText(input.value))
    );

    if (!isShadeGroup) return;

    // Hide the original Palo Alto legend
    const legend = swatchGroup.querySelector('.radio__legend');
    if (legend) {
      legend.style.display = 'none';
    }
const customTitle = document.querySelector(
  '.facelove-shade-finder__row .facelove-selected-shade-title'
);

if (!customTitle) return;

    customTitle.textContent =
      `SHADE: ${selectedValue.toUpperCase()}`;
  });
}

  function initializeAllShadeFinders() {
    document
      .querySelectorAll('[data-shade-finder]')
      .forEach(initializeShadeFinder);

    updateSelectedShadeTitle(document);
  }

  document.addEventListener('change', (event) => {
    if (
      event.target.matches(
        [
          'input[type="radio"]',
          'select[data-single-option-selector]',
          'select[name*="options"]'
        ].join(',')
      )
    ) {
      window.requestAnimationFrame(() => {
        updateSelectedShadeTitle(document);
      });
    }
  });

  document.addEventListener(
    'DOMContentLoaded',
    initializeAllShadeFinders
  );

  document.addEventListener(
    'shopify:section:load',
    initializeAllShadeFinders
  );

  document.addEventListener(
    'shopify:block:select',
    initializeAllShadeFinders
  );

  initializeAllShadeFinders();
})();